import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import axios, { AxiosError, type AxiosResponse } from 'axios'
import { bannerStore, EnumAlertBannerVariant } from 'shared/ui'
import { storeCache } from 'shared/store/StoreCache'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { IS_APP_LIGHT, IS_CALLING_APP_LIGHT } from 'shared/config'
import { logger, sendGTMEvent, trackOptibaseConversion } from 'shared/lib'
import type { AxiosOptions } from 'shared/api'
import { updateDesignV2Api } from 'entities/FeatureFlags/lib/updateDesignV2Api'
import type {
  IParamsAcceptAgencyInvite,
  IParamsAcceptMemberInvite,
  IParamsLogin,
  IParamsRegister,
  IParamsSendTwoFaCode,
  IParamsVerifyTwoFaCode,
  IResponseAuthRefresh,
  IResponseToken,
  IParamsSignUpRegister,
  IParamsSignUpOrganization,
} from 'entities/Auth'
import { AuthPath, RegisterPath } from 'entities/ExportRouters'
import { AuthApi } from 'entities/Auth/api'
import {
  getAssumeToken,
  getToken,
  removeTokenLocalStorage,
  setToken,
  setTokenLocalTwoFaStorage,
} from 'entities/Auth/lib/setToken'
import { clearSessionIntercom } from 'entities/Intercom'
import { UsersApi, type UsersStore } from 'entities/Users'
import {
  type IResponseProfile,
  type IResponseUsersProfilesUiSettings,
} from 'entities/Users/api/types'
import { type Websocket } from 'entities/WebSocket'
import { type UserSettingsStore } from 'entities/Settings'
import { type OrganizationStore } from 'entities/Organization'
import { TwoFaModalStore } from 'entities/Auth/ui/TwoFaModal'
import { sessionManager } from '../lib/sessionManager'
import { GoogleAuthStore } from './googleAuth'
import { type IOrganizationStatus, type IResponseGoogleAuth } from './types'
import { getBaseApiParams } from '../helpers/getBaseApiParams'

export class AuthStore {
  private _idOrgModal = 'orgModal'
  private _authRedirectUrl: null | string = null
  private _error = ''
  private _loading = true
  private _isAssume = false
  private _isLoggedIn = false
  private _isRefresh = false
  private _token = ''
  private _organizationStatus: IOrganizationStatus | null = null

  private _isNewSession = false
  private readonly _googleAuthStore: GoogleAuthStore
  private _external_source = ''
  private _closeAfterLogin = false
  private _disposeReactionOnLoginFromAppLight: IReactionDisposer | null = null
  private _twoFaModalStore: TwoFaModalStore

  constructor(
    private _usersStore: UsersStore,
    private _websocket: Websocket,
    private _userSettingsStore: UserSettingsStore,
    private _organizationStore: OrganizationStore
  ) {
    makeAutoObservable(this)
    this.initToken()
    this.checkAssuming()
    this.reactionOnLoginFromAppLight()
    this._twoFaModalStore = new TwoFaModalStore(this)
    this._googleAuthStore = new GoogleAuthStore(this.socialAuthCallback)
  }

  get isRefresh() {
    return this._isRefresh
  }

  get isLoggedIn() {
    return this._isLoggedIn
  }

  get hasToken() {
    return !!this._token
  }

  get hasOid() {
    const token = this._token
    if (!this._token) return null

    const [, tokenData] = token.split('.')

    try {
      const parsedData = JSON.parse(atob(tokenData))

      return !!parsedData.oid
    } catch (e) {
      logger.error(e)
      return false
    }
  }

  get authRedirectUrl() {
    return this._authRedirectUrl
  }

  resetLoggedIn = () => {
    this._googleAuthStore.resetGlobal()
  }

  setAuthRedirectUrl(value: string | null) {
    this._authRedirectUrl = value
  }

  initToken = () => {
    const token = getToken()
    if (token) this._token = token
  }

  socialAuthCallback = ({
    token,
    is_deleted_user,
    is_2fa,
    settings,
    owner_email_settings,
  }: IResponseGoogleAuth) => {
    if (is_2fa) {
      setTokenLocalTwoFaStorage(token.access_token)

      this._twoFaModalStore.openTwoFaModal({ settings, owner_email_settings })

      return
    }

    if (token) {
      this.validateToken(token, is_deleted_user)
    }
  }

  reactionOnLoginFromAppLight = () => {
    this._disposeReactionOnLoginFromAppLight?.()
    this._disposeReactionOnLoginFromAppLight = reaction(
      () => this.isLoggedIn,
      (isLoggedIn) => {
        if (isLoggedIn) {
          if (this._closeAfterLogin) {
            setTimeout(() => {
              window.close()
            }, 100)
          }
        }
      }
    )
  }

  getOrganizationStatus = async () => {
    try {
      const {
        data: { organization, organization_in_progress },
      } = await UsersApi.getUsersProfile()

      let status: IOrganizationStatus = organization_in_progress ? 'in_progress' : 'empty'
      if (organization) status = organization.completed ? 'completed' : 'in_progress'

      return status
    } catch (e) {
      return null
    }
  }

  // Identifies existing customers to www.salesmessage.com for Optibase A/B test exclusion.
  // To test as a non-customer: clear the sm_customer cookie in browser dev tools.
  private setCustomerCookie = () => {
    const expires = new Date()
    expires.setFullYear(expires.getFullYear() + 1)
    document.cookie = `sm_customer=1; Domain=.salesmessage.com; Path=/; Secure; SameSite=Lax; Expires=${expires.toUTCString()}`
  }

  setSuccessAuthToken = (token: string, withoutV2Logic?: boolean) => {
    this.setToken(token)
    this.setCustomerCookie()
    this.setLoggedIn(!!this.hasOid)
    if (!withoutV2Logic) {
      this.setDesignV2API()
      document.body.classList.remove('is-auth')
    }

    this._isNewSession = sessionManager.syncSession(true)
  }

  validateToken = async (token: IResponseToken, isDeletedUser?: boolean) => {
    const [, tokenData] = token.access_token.split('.')
    let parsedData = null

    try {
      parsedData = JSON.parse(atob(tokenData))
    } catch (e) {
      logger.error(e)
    }

    if (!parsedData.oid) {
      try {
        const {
          data: { data },
        }: AxiosResponse<IResponseUsersProfilesUiSettings> = await axios.get(
          'users/profiles/ui-settings/registration-steps',
          getBaseApiParams(token)
        )

        if (data || !isDeletedUser) {
          this.setSuccessAuthToken(token.access_token)
          return
        }

        modalStore.addModal({
          id: this._idOrgModal,
          type: ModalTypeList.WARNING,
          title: 'Oh-oh! Looks like the Salesmsg account you were part of is no longer available.',
          primaryAction: {
            text: 'Create a new account',
            onAction: () => {
              modalStore.closeModal(this._idOrgModal)
              this.setSuccessAuthToken(token.access_token)
            },
          },
          secondaryAction: {
            text: 'Cancel',
            onAction: () => {
              modalStore.closeModal(this._idOrgModal)
              this.removeToken()
            },
          },
        })
      } catch (e) {
        logger.error(e)
      }
    } else {
      await this.logGTMLogin(token)
      this.setSuccessAuthToken(token.access_token)
    }
  }

  async login(body: IParamsLogin, options: AxiosOptions) {
    const { data } = await AuthApi.login(body, options)

    if (data.data?.is_2fa) {
      return data.data
    }

    if (data.token) {
      await this.validateToken(data.token, true)
    }
  }

  setGoogleAuthStore = (data: IResponseToken) => {
    this.setSuccessAuthToken(data.access_token)
  }

  async register(body: IParamsRegister) {
    const { data } = await AuthApi.register(body)

    if (data.token) {
      this.setSuccessAuthToken(data.token.access_token)
    }

    return data
  }

  async signUpRegister(body: IParamsSignUpRegister, options: AxiosOptions) {
    const { data } = await AuthApi.signUpRegister(body, options)

    if (data.token) {
      this.setSuccessAuthToken(data.token.access_token)
      trackOptibaseConversion('trial_signup')
    }

    return data
  }

  async signUpOrganization(body: IParamsSignUpOrganization, options: AxiosOptions) {
    const { data } = await AuthApi.signUpOrganization(body, options)
    return data
  }

  async acceptMemberInvite(body: IParamsAcceptMemberInvite) {
    const { data } = await AuthApi.acceptMemberInvite(body)

    if (data.token) {
      this.setSuccessAuthToken(data.token.access_token)
    }

    return data
  }

  async acceptAgencyInvite(body: IParamsAcceptAgencyInvite) {
    const { data } = await AuthApi.acceptAgencyInvite(body)

    if (data.token) {
      this.setSuccessAuthToken(data.token.access_token)
    }

    return data
  }

  checkAssuming = () => {
    this._isAssume = !!getAssumeToken()

    if (this._isAssume) {
      bannerStore.add({
        id: nanoid(),
        action: {
          text: 'Stop assuming',
          onAction: () => {
            window.close()
          },
        },
        alert: {
          title: 'You have assumed another user’s account',
          variant: EnumAlertBannerVariant.Warning,
          ariaLabel: 'Assuming',
        },
      })
    }
  }

  setToken = (token: string) => {
    setToken(token)
    this._token = token
  }
  removeToken = () => {
    removeTokenLocalStorage(this._isAssume)
    this._token = ''
  }

  setLoggedIn = (value: boolean) => {
    this._isLoggedIn = value
  }

  private _fetchLogout = async () => {
    try {
      await AuthApi.logout()
    } catch (e) {
      logger.error(e)
    }
  }

  logout = async () => {
    storeCache.dispose()
    clearSessionIntercom()

    if (this._isAssume) {
      window.close()
      return
    }
    try {
      localStorage.removeItem('showedAwayStatusDate')
      localStorage.removeItem('contactDetailsOpenStatus')
      localStorage.setItem(`first_logout_${this._usersStore.user_owner_id}`, '1')
      await this._fetchLogout()
      this.removeToken()
      bannerStore.dispose()
      this._websocket.disconnect()
      this.setLoggedIn(false)
      window.userpilot?.destroy?.()
    } catch (e) {
      logger.error(e)
    } finally {
      if (
        window.location.href.includes(AuthPath.login) ||
        window.location.pathname === `/${RegisterPath.root}`
      )
        return

      if (IS_APP_LIGHT || IS_CALLING_APP_LIGHT) {
        window.location.reload()
        return
      }

      window.stop()
      window.location.href = `/${AuthPath.login}`
    }
  }

  refreshCallingLight = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await AuthApi.updateAuthRefresh()
      const isLogin = Boolean(data.is_login)

      if (!isLogin) {
        this.removeToken()
        this.setLoggedIn(false)
      }

      if (isLogin) {
        this.setToken(data.token.access_token)
        this.setLoggedIn(!!this.hasOid)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  refresh = async ({
    enableLoader = true,
    isTryRefreshWithoutToken = false,
    enabledChangeDomainApi = false,
  } = {}): Promise<IResponseAuthRefresh | undefined> => {
    try {
      if (enableLoader) {
        runInAction(() => {
          this._loading = true
        })
      }

      const { data } = await AuthApi.updateAuthRefresh({
        enabledChangeDomainApi: enabledChangeDomainApi,
      })
      const isLogin = Boolean(data.is_login)

      this._isRefresh = data.is_login

      if (isTryRefreshWithoutToken && !isLogin && getToken()) {
        this.removeToken()
        return await this.refresh()
      }

      runInAction(() => {
        if (!isLogin) {
          this.removeToken()
          this.setLoggedIn(false)
        } else {
          this.setCustomerCookie()
          this.setLoggedIn(!!this.hasOid)
        }

        if (enableLoader) {
          this._isNewSession = sessionManager.isNewSession()
          sessionManager.syncSession(false)
        }
      })

      return data
    } catch (e) {
      if (e instanceof AxiosError) {
        if (this._organizationStore.isHipaaCompliant) {
          window.sessionStorage.setItem('expire_time_session', '1')
          this.logout()

          return
        }
      }

      runInAction(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this._error = e.stack || 'Error'
      })
    } finally {
      if (enableLoader) {
        runInAction(() => {
          this._loading = false
        })
      }
    }
  }

  sendTwoFaCode = async (params: IParamsSendTwoFaCode, isAuthorized = false) => {
    const apiCall = isAuthorized ? AuthApi.sendTwoFaCodeAuthorized : AuthApi.sendTwoFaCode
    const { data } = await apiCall(params)

    return data
  }

  verifyTwoFaCode = async (params: IParamsVerifyTwoFaCode, isAuthorized = false) => {
    const apiCall = isAuthorized ? AuthApi.verifyTwoFaCodeAuthorized : AuthApi.verifyTwoFaCode
    const { data } = await apiCall(params)

    if (data.token) {
      this.setSuccessAuthToken(data.token.access_token)
    }

    return data
  }

  setExternalSource = (external_source: string) => {
    this._external_source = external_source
  }

  setCloseAfterLogin = (value: boolean) => {
    this._closeAfterLogin = value
  }

  setDesignV2API = () => {
    if (!this.hasOid) {
      updateDesignV2Api({
        is_enable_new_design: true,
        sign_up_v2: true,
      })
    } else {
      updateDesignV2Api({
        is_enable_new_design: this._userSettingsStore.isSalesmsgDesignV2,
      })
    }
  }

  get isAssume() {
    return this._isAssume
  }

  get error() {
    return this._error
  }

  get isNewSession() {
    return this._isNewSession
  }

  get googleAuthStore() {
    return this._googleAuthStore
  }

  setOnGoogleSignUpSuccess = (callback: ((payload: IResponseGoogleAuth) => void) | null) => {
    this._googleAuthStore.setOnSignUpSuccess(callback)
  }

  get closeAfterLogin() {
    return this._closeAfterLogin
  }

  logGTMLogin = async (token: IResponseToken) => {
    try {
      const {
        data: { email, id, number },
      }: AxiosResponse<IResponseProfile> = await axios.get('users/profile', getBaseApiParams(token))

      sendGTMEvent({ eventName: 'signed_in', eventParams: { email, userId: id, phone: number } })
    } catch (e) {
      logger.error(e)
    }
  }
}
